import { service } from '@ember/service';
import type { AsyncBelongsTo } from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { all, dropTask, task } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import type DeviceModel from 'garaje/models/device';
import type LocationModel from 'garaje/models/location';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import _uniqBy from 'lodash/uniqBy';

interface GlobalDevicesIpadsMoveLocationModalComponentSignature {
  Args: {
    selectedIPads: DeviceModel[];
    operationSuccessful: () => void;
    hideModal: () => void;
    locations: SkinnyLocationModel[];
  };
}

export default class GlobalDevicesIpadsMoveLocationModalComponent extends Component<GlobalDevicesIpadsMoveLocationModalComponentSignature> {
  @service declare flashMessages: FlashMessagesService;
  defaultLocationFilter = { name: 'Choose a location' };

  @tracked selectedLocation?: SkinnyLocationModel;
  @tracked name?: string;

  get uniqLocations(): SkinnyLocationModel[] {
    return _uniqBy(this.args.locations, 'id');
  }

  changeLocationTask = task(async (ipad: DeviceModel, skinnyLocation: SkinnyLocationModel) => {
    try {
      const realLocation = await skinnyLocation.realLocation();
      ipad.location = <AsyncBelongsTo<LocationModel>>(<unknown>realLocation);
      await ipad.save();
    } catch (error) {
      throw Error(parseErrorForDisplay(error));
    }
  });

  changeLocationsTask = dropTask(async () => {
    const newLocation = this.selectedLocation;
    const iPads = this.args.selectedIPads;
    const changeLocationTask = this.changeLocationTask;

    try {
      await all(iPads.map((ipad) => changeLocationTask.perform(ipad, newLocation!)));

      const successMessage = `Successfully updated ${pluralize(iPads.length, 'iPad')}`;
      this.flashMessages.showFlash('success', successMessage);

      this.args.operationSuccessful();
      this.args.hideModal();
    } catch (error) {
      this.flashMessages.showFlash('error', parseErrorForDisplay(error));
    }
  });
}
